import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useSEO from '../../useSEO';
import { useSite } from '../../components/StoreLayout';
import { STORE_BASE } from '../../routeBase';
import THEMES from './socialCaptionData';

const lbl = { fontSize: 12.5, fontWeight: 600, color: 'var(--sf-primary)', display: 'block', marginBottom: 5 };
const inp = { width: '100%', padding: '9px 11px', border: '1.5px solid #e5e5e5', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit' };

export default function SocialCaptionGenerator() {
  const { content: c } = useSite();
  useSEO({
    title: c['tools.social-caption.meta_title'] || 'Social Media Caption & Hashtag Generator for Opticians',
    description: c['tools.social-caption.meta_description'] || 'Free social media caption and hashtag generator for optical retailers — pick a theme and get ready-to-post Instagram and Facebook captions in seconds.',
    keywords: c['tools.social-caption.meta_keywords'] || 'social media caption generator, optical store instagram captions, eyewear hashtags, optician social media, retail marketing tools',
    canonical: `${window.location.origin}/store/tools/social-caption-generator`,
  });

  const [themeKey, setThemeKey] = useState(THEMES[0].key);
  const [shopName, setShopName] = useState('');
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [copiedTags, setCopiedTags] = useState(false);

  const theme = THEMES.find(t => t.key === themeKey) || THEMES[0];
  const shop = shopName.trim() || 'our store';
  const captions = useMemo(() => theme.captions.map(c => c.split('{shop}').join(shop)), [theme, shop]);
  const hashtagLine = theme.hashtags.join(' ');

  const copy = async (text, mark) => {
    try {
      await navigator.clipboard.writeText(text);
      mark();
      setTimeout(() => { setCopiedIdx(null); setCopiedTags(false); }, 1800);
    } catch (e) { /* clipboard unavailable — user can still select+copy manually */ }
  };

  const card = { background: '#fff', border: '1px solid #eee', borderRadius: 14, padding: 24 };

  return (
    <div style={{ background: 'var(--sf-body-bg)' }}>
      {/* HERO */}
      <section style={{ maxWidth: 900, margin: '0 auto', padding: '48px 24px 16px', textAlign: 'center' }}>
        <Link to={`${STORE_BASE}/tools`} style={{ fontSize: 12.5, color: 'var(--sf-hover)', fontWeight: 600, textDecoration: 'none' }}>
          <i className="ti ti-arrow-left" style={{ fontSize: 13, verticalAlign: -1 }} /> All free tools
        </Link>
        <h1 style={{ fontSize: 'clamp(24px,3.6vw,34px)', fontWeight: 800, color: 'var(--sf-primary)', margin: '14px 0 12px' }}>
          Social Caption &amp; Hashtag Generator
        </h1>
        <p style={{ fontSize: 14.5, color: 'var(--sf-muted)', maxWidth: 620, margin: '0 auto', lineHeight: 1.7 }}>
          Pick a theme, get three ready-to-post captions and a hashtag set — copy, paste, done.
        </p>
      </section>

      <section style={{ maxWidth: 760, margin: '24px auto 0', padding: '0 24px' }}>
        <div style={{ ...card, marginBottom: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: 12 }} className="scg-grid">
            <div>
              <label style={lbl}>Theme</label>
              <select value={themeKey} onChange={e => setThemeKey(e.target.value)} style={inp}>
                {THEMES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Your Store Name (optional)</label>
              <input type="text" value={shopName} onChange={e => setShopName(e.target.value)} placeholder="e.g. Sharma Opticals" style={inp} />
            </div>
          </div>
        </div>

        {captions.map((c, i) => (
          <div key={i} style={{ ...card, marginBottom: 14, display: 'flex', gap: 14, alignItems: 'flex-start' }}>
            <p style={{ flex: 1, margin: 0, fontSize: 14, color: 'var(--sf-primary)', lineHeight: 1.7 }}>{c}</p>
            <button
              onClick={() => copy(c, () => setCopiedIdx(i))}
              style={{
                flexShrink: 0, border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
                background: copiedIdx === i ? '#16a34a' : 'var(--sf-primary)', color: '#fff',
              }}
            >
              {copiedIdx === i ? 'Copied ✓' : 'Copy'}
            </button>
          </div>
        ))}

        <div style={{ ...card, display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--sf-hover)', marginBottom: 6 }}>Hashtags</div>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--sf-muted)', lineHeight: 1.7 }}>{hashtagLine}</p>
          </div>
          <button
            onClick={() => copy(hashtagLine, () => setCopiedTags(true))}
            style={{
              flexShrink: 0, border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
              background: copiedTags ? '#16a34a' : 'var(--sf-primary)', color: '#fff',
            }}
          >
            {copiedTags ? 'Copied ✓' : 'Copy all'}
          </button>
        </div>
      </section>

      {/* CTA BANNER */}
      <section style={{ background: 'var(--sf-primary)', color: '#fff', marginTop: 48 }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 24px', display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h3 style={{ fontSize: 17, fontWeight: 800, margin: '0 0 6px' }}>Stock up your shop for less</h3>
            <p style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.75)', margin: 0, maxWidth: 460 }}>
              View our latest wholesale frame collections — acetate, TR90 and metal, shipped worldwide.
            </p>
          </div>
          <Link to={`${STORE_BASE}/shop`} style={{
            background: '#fff', color: 'var(--sf-primary)', padding: '11px 22px', borderRadius: 8,
            fontWeight: 700, fontSize: 13, textDecoration: 'none', flexShrink: 0,
          }}>
            Browse Wholesale Catalog
          </Link>
        </div>
      </section>

      <style>{`
        @media (max-width: 600px) {
          .scg-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
